/**
 * Online Booking enum mappings and utilities
 * Centralized mappings for online booking configuration options
 */

import { OnlineBookingMode, ServiceSelectionMode, WorkerSelectionMode } from "@salon/types";
import type { TFunction } from "i18next";
import { CheckCircle, Clock, UserCheck } from "lucide-react";

export interface EnumOption<T> {
  value: T;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  badge: string;
}

export const ONLINE_BOOKING_MODE_OPTIONS: EnumOption<OnlineBookingMode>[] = [
  {
    value: OnlineBookingMode.AUTO_ACCEPT,
    label: "Automatically Accept",
    description: "Bookings are immediately confirmed without review",
    icon: CheckCircle,
    badge: "Instant",
  },
  {
    value: OnlineBookingMode.AUTO_ACCEPT_ASSIGN,
    label: "Auto Accept & Assign",
    description: "Bookings are confirmed and automatically assigned to available workers",
    icon: UserCheck,
    badge: "Smart",
  },
  {
    value: OnlineBookingMode.MANUAL,
    label: "Manual Review",
    description: "All bookings require manual approval from staff",
    icon: Clock,
    badge: "Review",
  },
];

/**
 * Get the display option for a given enum value
 */
export function getOnlineBookingModeOption(
  value: OnlineBookingMode
): EnumOption<OnlineBookingMode> | undefined {
  return ONLINE_BOOKING_MODE_OPTIONS.find((option) => option.value === value);
}

/**
 * Validate and normalize enum values from string (for values coming from database)
 */
export function normalizeOnlineBookingMode(value: string): OnlineBookingMode {
  // Ensure the value matches one of the enum values
  const normalizedValue = value as OnlineBookingMode;
  if (Object.values(OnlineBookingMode).includes(normalizedValue)) {
    return normalizedValue;
  }

  // Fallback to default if invalid
  console.warn(
    `Invalid OnlineBookingMode value: "${value}". Using default: ${OnlineBookingMode.AUTO_ACCEPT}`
  );
  return OnlineBookingMode.AUTO_ACCEPT;
}

export function normalizeServiceSelectionMode(value: string): ServiceSelectionMode {
  // Ensure the value matches one of the enum values
  const normalizedValue = value as ServiceSelectionMode;
  if (Object.values(ServiceSelectionMode).includes(normalizedValue)) {
    return normalizedValue;
  }

  // Fallback to default if invalid
  console.warn(
    `Invalid ServiceSelectionMode value: "${value}". Using default: ${ServiceSelectionMode.MULTIPLE}`
  );
  return ServiceSelectionMode.MULTIPLE;
}

export function normalizeWorkerSelectionMode(value: string): WorkerSelectionMode {
  // Ensure the value matches one of the enum values
  const normalizedValue = value as WorkerSelectionMode;
  if (Object.values(WorkerSelectionMode).includes(normalizedValue)) {
    return normalizedValue;
  }

  // Fallback to default if invalid
  console.warn(
    `Invalid WorkerSelectionMode value: "${value}". Using default: ${WorkerSelectionMode.PRIMARY_SERVICE}`
  );
  return WorkerSelectionMode.PRIMARY_SERVICE;
}

/**
 * Helper functions to create translated versions of enum options
 */
export function getTranslatedOnlineBookingModeOptions(
  t: TFunction
): EnumOption<OnlineBookingMode>[] {
  return ONLINE_BOOKING_MODE_OPTIONS.map((option) => ({
    ...option,
    label: t(option.label),
    description: t(option.description),
    badge: t(option.badge),
  }));
}
